<template>
 <div> <!-- Only one element for Transition -->
  <div class="row">
      <div class="col-12 col-sm-6">
        <SelectInputComponent
          value-key="userInfo.civility"
          label="Civilité"
          placeholder="Sélectionnez une civilité"
          :required="true"
          class="mb-3"
          :options="Object.values(CivilityEnum).map(type => ({ value: type, label: GetCivilityShortLabel(type) }))"
        />
      </div>
      <div class="col-12 col-sm-6" v-if="participation?.travel?.userStatusRequired">
        <SelectInputComponent
          value-key="userInfo.status"
          label="Je suis"
          placeholder="Sélectionnez un statut"
          :required="true"
          class="mb-3"
          :options="Object.values(participation?.travel?.usersStatusAvailable || UserStatusEnum).map(type => ({ value: type, label: GetUserStatusLabel(type) }))"
          @change="onUserStatusChange"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-sm-6">
        <TextInputComponent
          value-key="userInfo.lastName"
          label="Nom"
          placeholder="Nom"
          :required="true"
          class="mb-3"
          :upper-case="true"
        />
      </div>
      <div class="col-12 col-sm-6">
        <TextInputComponent
          value-key="userInfo.firstName"
          label="Prénom"
          placeholder="Prénom"
          :required="true"
          class="mb-3"
          :capitalize="true"
        />
      </div>
      <div class="col-12 col-md-6">
        <TextInputComponent
          value-key="userInfo.email"
          label="Email"
          placeholder="Email"
          :required="true"
          class="mb-3"
          type="email"
        />
      </div>
      <div class="col-12 col-md-6">
        <PhoneInputComponent
          value-key="userInfo.mobilePhone"
          label="Téléphone"
          placeholder="Téléphone"
          class="mb-3"
          v-model:is-valid="isMobilePhoneValid"
          :required="true"
        />
      </div>
  </div>

  <template v-if="participation?.travel.addressRequired">
    <div class="row">
      <div class="col-12">
        <TextInputComponent
          value-key="userInfo.address"
          label="Adresse"
          placeholder="Adresse"
          :required="true"
          class="mb-3"
        />
      </div>
      <div class="col-12 col-md-6">
        <TextInputComponent
          value-key="userInfo.postalCode"
          label="Code postal"
          placeholder="Code postal"
          :required="true"
          class="mb-3"
          type="text"
        />
      </div>
      <div class="col-12 col-md-6">
        <TextInputComponent
          value-key="userInfo.city"
          label="Ville"
          placeholder="Ville"
          :required="true"
          class="mb-3"
          :upper-case="true"
        />
      </div>
    </div>
  </template>

  <hr class="pb-1" v-if="participation?.travel?.passeportRequired || participation?.travel?.estaRequired || participation?.travel?.nationalityRequired || participation?.travel?.birthDateRequired" />
  <div class="row mt-md-4">
      <div class="col-12 col-md-6" v-if="participation?.travel?.nationalityRequired">
        <SelectInputComponent
          value-key="userInfo.nationality"
          label="Nationalité"
          placeholder="Sélectionnez une nationalité"
          :required="true"
          class="mb-3"
          :options="Nationalities"
        />
      </div>
      <div class="col-12 col-md-6" v-if="participation?.travel?.birthDateRequired">
        <DatePickerComponent
          value-key="userInfo.birthDate"
          label="Date de naissance"
          placeholder="Date de naissance"
          :required="true"
          class="mb-3"
          :max-date="new Date()"
        />
      </div>
    </div>
    <template v-if="participation?.travel?.passeportRequired">
      <div class="row mt-md-4">
        <div class="col-12 col-md-6">
          <TextInputComponent
            value-key="userInfo.passportNumber"
            label="Numéro de passeport"
            placeholder="Numéro de passeport"
            :required="true"
            class="mb-3"
            :upper-case="true"
          />
        </div>
        <div class="col-12 col-md-6">
          <DatePickerComponent
            value-key="userInfo.passportExpirationDate"
            label="Date d'expiration du passeport"
            placeholder="Date d'expiration du passeport"
            :required="false"
            class="mb-3"
            :min-date="passeportMinDate"
          />
        </div>
        <div class="col-12">
          <i class="fa-solid fa-triangle-exclamation text-warning fa-lg px-2"></i>Vous devez être en possession d'un passeport valide jusqu'à <span v-html="participation?.travel?.passeportSixMonthsRequired == true ? '6 mois après la date de retour, soit le ' + formatDate(passeportMinDate,'D MMM YYYY') : 'la date de retour'"></span>.
        </div>
      </div>
    </template>
    <template v-if="participation?.travel?.estaRequired">
      <hr class="pb-1" />
      <div class="row mt-md-4">
        <div class="col-12 col-md-6">
          <TextInputComponent
            value-key="userInfo.estaNumber"
            label="Numéro ESTA"
            placeholder="Numéro ESTA"
            :required="true"
            name="esta-authorization"
            autocomplete="one-time-code"
            class="mb-3"
          />
        </div>
        <div class="col-12">
          <i class="fa-solid fa-triangle-exclamation text-warning fa-lg px-2"></i>Vous devez être en possession d'un ESTA valide pour cet événement.
        </div>
      </div>
    </template>

    <template v-if="participation?.travel.infoFields && participation.travel.infoFields.length > 0">
      <hr class="pb-1" />
      <div class="row mt-md-4">
        <template v-for="(infoField,idx) in participation.travel.infoFields" :key="infoField.id">
          <div :class="[infoField.type == TravelDbFieldTypeEnum.TextArea ? 'col-12' : 'col-12 col-sm-6']" v-if="[TravelDbFieldTypeEnum.Text,TravelDbFieldTypeEnum.TextArea].includes(infoField.type)">
            <TextInputComponent
              :value-key="`userInfo.infoFields[${idx}].value`"
              :label="infoField.label"
              :placeholder="infoField.label"
              :required="infoField.required"
              class="mb-3"
              :type="infoField.type == 'TextArea' ? 'multiline' : 'text'"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Date].includes(infoField.type)">
            <DatePickerComponent
              :value-key="`userInfo.infoFields[${idx}].value`"
              :label="infoField.label"
              :placeholder="infoField.label"
              :required="infoField.required"
              class="mb-3"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Number].includes(infoField.type)">
            <NumberInputComponent
              :value-key="`userInfo.infoFields[${idx}].value`"
              :value-as-string="true"
              :label="infoField.label"
              :placeholder="infoField.label"
              :required="infoField.required"
              class="mb-3"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Boolean].includes(infoField.type)">
            <ToggleCheckboxComponent
              :value-key="`userInfo.infoFields[${idx}].value`"
              :value-as-string="true"
              :inverseOrder="true"
              labelTrue="Oui"
              labelFalse="Non"
              :label="infoField.label"
              :title="infoField.label"
              :required="infoField.required"
              class="mb-3"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Choice].includes(infoField.type)">
            <SelectInputComponent
              :value-key="`userInfo.infoFields[${idx}].value`"
              :label="infoField.label"
              :placeholder="infoField.label"
              :required="infoField.required"
              :emptyOptionLabel="'Sélectionnez une option'"
              :options="infoField.choices?.map((option: any) => ({ value: option, label: option })) ?? []"
              class="mb-3"
            />
          </div>
        </template>
      </div>
    </template>

    <template v-if="participation?.travel?.emergencyContactRequested">
      <hr class="pb-1" />
      <div class="row">
        <h6 class="text-decoration-underline">Personne à prévenir en cas d'urgence</h6>
        <div class="col-12 col-sm-6">
          <TextInputComponent
            value-key="userInfo.emergencyContactLastname"
            label="Nom"
            placeholder="Nom"
            :required="false"
            class="mb-3"
            :upper-case="true"
          />
        </div>
        <div class="col-12 col-sm-6">
          <TextInputComponent
            value-key="userInfo.emergencyContactFirstname"
            label="Prénom"
            placeholder="Prénom"
            :required="false"
            class="mb-3"
            :capitalize="true"
          />
        </div>
        <div class="col-12 col-md-6">
          <TextInputComponent
            value-key="userInfo.emergencyContactEmail"
            label="Email"
            placeholder="Email"
            :required="false"
            class="mb-3"
            type="email"
          />
        </div>
        <div class="col-12 col-md-6">
          <PhoneInputComponent
            value-key="userInfo.emergencyContactPhone"
            label="Téléphone"
            placeholder="Téléphone"
            class="mb-3"
            v-model:is-valid="isEmergencyMobilePhoneValid"
            :required="false"
          />
        </div>
      </div>
    </template>

    <PageBottomMessage messageKey="pageInformations" :discreet="true"></PageBottomMessage>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue';
import { GetCivilityShortLabel, GetUserStatusLabel,formatDate } from '@/compositions/helper';
import { Nationalities } from '@/compositions/nationalities';
import { usePostalCode } from '@/compositions/usePostalCode';
import { useField } from 'vee-validate';

const participation = defineModel<UserParticipation>();
const isMobilePhoneValid = defineModel<boolean>('isMobilePhoneValid');
const isEmergencyMobilePhoneValid = defineModel<boolean>('isEmergencyMobilePhoneValid');

const { setValue: setAddressCity,  } = useField<string>(() => 'userInfo.city');
const { setValue: setInvoiceCompany,  } = useField<string>(() => 'invoice.company');
const { setValue: setInvoiceShopCode,  } = useField<string>(() => 'invoice.shopCode');
const { setValue: setInvoiceLastName,  } = useField<string>(() => 'invoice.lastName');
const { setValue: setInvoiceFirstName,  } = useField<string>(() => 'invoice.firstName');
const { setValue: setInvoiceAddress,  } = useField<string>(() => 'invoice.address');
const { setValue: setInvoiceZipCode,  } = useField<string>(() => 'invoice.zipCode');
const { setValue: setInvoiceCity,  } = useField<string>(() => 'invoice.city');
const { setValue: setInvoiceCountry,  } = useField<string>(() => 'invoice.country');


const postalCodeRef = toRef(() => participation.value?.userInfo?.postalCode)
usePostalCode(postalCodeRef, (city) => setAddressCity(city))

const passeportMinDate = computed(() => {
  if(participation.value?.travel?.passeportSixMonthsRequired) {
    const endDate = participation.value.travel.endDate ? new Date(participation.value.travel.endDate) : new Date();
    endDate.setMonth(endDate.getMonth() + 6);
    endDate.setDate(endDate.getDate() + 1);
    return endDate;
  }
  const result = participation.value?.travel?.endDate ? new Date(participation.value.travel.endDate) : new Date();
  result.setDate(result.getDate() + 1);
  return result;
});

const onUserStatusChange = (newStatus: UserStatusEnum) => {
  if(newStatus === UserStatusEnum.Organisation) {
    setInvoiceCompany('NEMOPROD');
    setInvoiceShopCode('0');
    setInvoiceLastName('ELALOUF');
    setInvoiceFirstName('Alexandre');
    setInvoiceAddress('7 rue Alfred Roll');
    setInvoiceZipCode('75017');
    setInvoiceCity('PARIS');
    setInvoiceCountry('FR');
  }
}
</script>
